import { tokenManager } from '../utils/api';
import { USER_URL } from '../config/environment';
import axios from 'axios';

/**
 * Analytics Service
 * Handles all analytics-related API calls
 */

export const analyticsService = {
  /**
   * Get global active users
   * @returns {Promise<Object>} Active users data
   */
  getGlobalActiveUsers: async () => {
    const response = await axios.get(`${USER_URL}/api/analytics/global/active-users`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get global active accounts
   * @returns {Promise<Object>} Active accounts data
   */
  getGlobalActiveAccounts: async () => {
    const response = await axios.get(`${USER_URL}/api/analytics/global/active-accounts`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get global total short URLs
   * @returns {Promise<Object>} Total short URLs data
   */
  getGlobalTotalShortUrls: async () => {
    const response = await axios.get(`${USER_URL}/api/analytics/global/shorturls/total`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get global top accounts by URLs
   * @returns {Promise<Object>} Top accounts by URLs data
   */
  getGlobalTopAccountsByUrls: async () => {
    const response = await axios.get(`${USER_URL}/api/analytics/global/top-accounts/shorturls`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get global top accounts by credit
   * @returns {Promise<Object>} Top accounts by credit data
   */
  getGlobalTopAccountsByCredit: async () => {
    const response = await axios.get(`${USER_URL}/api/analytics/global/top-accounts/credit`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get global top campaigns
   * @returns {Promise<Object>} Top campaigns data
   */
  getGlobalTopCampaigns: async () => {
    const response = await axios.get(`${USER_URL}/api/analytics/global/top-campaigns`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get global top sender IDs
   * @returns {Promise<Object>} Top sender IDs data
   */
  getGlobalTopSenderIds: async () => {
    const response = await axios.get(`${USER_URL}/api/analytics/global/top-senderids`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get global monthly short URLs
   * @returns {Promise<Object>} Monthly short URLs data
   */
  getGlobalMonthlyShortUrls: async () => {
    const response = await axios.get(`${USER_URL}/api/analytics/global/shorturls/monthly`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get account active users
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account active users data
   */
  getAccountActiveUsers: async (accountId) => {
    const response = await axios.get(`${USER_URL}/api/analytics/account/${accountId}/active-users`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get account total short URLs
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account total short URLs data
   */
  getAccountTotalShortUrls: async (accountId) => {
    const response = await axios.get(`${USER_URL}/api/analytics/account/${accountId}/shorturls/total`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get account API keys count
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account API keys count data
   */
  getAccountApiKeysCount: async (accountId) => {
    const response = await axios.get(`${USER_URL}/api/analytics/account/${accountId}/apikeys/count`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get account credit balance
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account credit balance data
   */
  getAccountCreditBalance: async (accountId) => {
    const response = await axios.get(`${USER_URL}/api/analytics/account/${accountId}/credits/balance`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get account top campaigns
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account top campaigns data
   */
  getAccountTopCampaigns: async (accountId) => {
    const response = await axios.get(`${USER_URL}/api/analytics/account/${accountId}/top-campaigns`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get account top sender IDs
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account top sender IDs data
   */
  getAccountTopSenderIds: async (accountId) => {
    const response = await axios.get(`${USER_URL}/api/analytics/account/${accountId}/top-senderids`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get account browser distribution
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account browser distribution data
   */
  getAccountBrowserDistribution: async (accountId) => {
    const response = await axios.get(`${USER_URL}/api/analytics/account/${accountId}/clicks/browser-distribution`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get account location distribution
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account location distribution data
   */
  getAccountLocationDistribution: async (accountId) => {
    const response = await axios.get(`${USER_URL}/api/analytics/account/${accountId}/clicks/location-distribution`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get account device distribution
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Account device distribution data
   */
  getAccountDeviceDistribution: async (accountId) => {
    const response = await axios.get(`${USER_URL}/api/analytics/account/${accountId}/clicks/device-distribution`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  }
};

export default analyticsService;
